import { useState, useEffect } from "react";

import { faShieldAlt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Card } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import "./TwoFactorVerifyPage.scss";

import { handleSuccess, handleError } from "@/components/LoginStatusModal/LoginStatusModal";

import niraLoginBg from "../../assets/NIRALogInBgLogo.webp";
import niraLogoLong from "../../assets/NIRALogo_long.webp";
import { useAuth } from "../../context/AuthContext";
import { checkAuthFunction } from "../../services/auth";
import { getUserOrganization } from "../../services/roles";
import { verify2FALogin, handle2FAError } from "../../services/twoFactor";



function TwoFactorVerifyPage() {
  const [token, setToken] = useState("");
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { setRequires2FA, fetchUserInfo } = useAuth();

  // Check if user should be on this page
  useEffect(() => {
    const checkSession = async () => {
      try {
        const response = await checkAuthFunction();
        if (response.isAuthenticated) {
          // User is already authenticated, redirect to appropriate page
          const roleResponse = await getUserOrganization(response.user.id);
          const userRole = roleResponse.role || "";
          navigate(userRole === "CxO" ? "/executive-dashboard" : "/main");
        }
      } catch {
        // Session not authenticated yet, which is expected for 2FA flow
      }
    };

    checkSession();
  }, [navigate]);

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate token format
    const expectedLength = useBackupCode ? 8 : 6;
    if (token.length !== expectedLength) {
      setError(`Please enter a ${expectedLength}-${useBackupCode ? "character" : "digit"} code`);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await verify2FALogin(Number(token), useBackupCode);

      if (response.success) {
        // Clear 2FA requirement state
        setRequires2FA(false);

        // Show success message
        handleSuccess();

        // Small delay to allow session to be established
        setTimeout(async () => {
          try {
            // Fetch user info and update AuthContext state
            // This will set isAuthenticated, username, and role in AuthContext
            const success = await fetchUserInfo();

            if (success) {
              // Fetch user info succeeded, now get role for redirect
              const authResponse = await checkAuthFunction();
              if (authResponse?.user) {
                const roleResponse = await getUserOrganization(authResponse.user.id);
                const userRole = roleResponse.role || "";

                // Redirect based on role
                if (userRole === "CxO") {
                  navigate("/executive-dashboard");
                } else {
                  navigate("/main");
                }
              } else {
                navigate("/main");
              }
            } else {
              // fetchUserInfo failed - session might not be ready yet
              // Try again after a longer delay
              setTimeout(async () => {
                const retrySuccess = await fetchUserInfo();
                if (retrySuccess) {
                  const authResponse = await checkAuthFunction();
                  if (authResponse?.user) {
                    const roleResponse = await getUserOrganization(authResponse.user.id);
                    const userRole = roleResponse.role || "";
                    navigate(userRole === "CxO" ? "/executive-dashboard" : "/main");
                  } else {
                    navigate("/main");
                  }
                } else {
                  // Still failed, redirect anyway and let ProtectedRoute handle it
                  navigate("/main");
                }
              }, 500);
            }
          } catch {
            // On error, still try to navigate - ProtectedRoute will handle auth check
            navigate("/main");
          }
        }, 100);
      } else {
        setError(response.message || "Verification failed");
      }
    } catch (error: any) {
      const errorMessage = handle2FAError(error);
      setError(errorMessage);

      // Handle specific errors
      if (error.response?.status === 429) {
        handleError("Too many attempts. Please wait 1 minute before trying again.");
      } else if (error.response?.status === 400 && errorMessage.includes("expired")) {
        handleError("Session expired. Please login again.");
        setTimeout(() => navigate("/login"), 2000);
      } else {
        handleError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleCodeType = () => {
    setUseBackupCode(!useBackupCode);
    setToken("");
    setError(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (useBackupCode) {
      // Backup code: 8 hex characters
      const sanitized = value.toLowerCase().replace(/[^a-f0-9]/g, "");
      setToken(sanitized.slice(0, 8));
    } else {
      // TOTP: 6 digits
      const sanitized = value.replace(/\D/g, "");
      setToken(sanitized.slice(0, 6));
    }
  };

  return (
    <div>
      <section className="verify-input-container">
        <div className="geometric-vector-wrapper">
          <div className="geometric-vector"></div>
        </div>
        <div className="geometric-vector-wrapper">
          <div className="geometric-vector-1"></div>
        </div>
        <div className="geometric-vector-wrapper">
          <div className="geometric-vector-2"></div>
        </div>

        <div className="depth-effect-overlay"></div>

        <img src={niraLoginBg} alt="NIRA Background Logo" className="background-logo" />

        {/* VERIFICATION CARD */}
        <div className="card-container">
          <Card className="verify-card">
            <section className="d-flex flex-column justify-content-center align-items-center">
              <img src={niraLogoLong} alt="NIRA Logo" className="verify-logo mt-4" />
              <div className="security-icon-wrapper">
                <FontAwesomeIcon icon={faShieldAlt} className="security-icon" />
              </div>
              <h4 className="verify-title">Two-Factor Authentication</h4>
              <p className="p-small">
                {useBackupCode
                  ? "Enter one of your backup codes"
                  : "Enter the code from your authenticator app"}
              </p>
            </section>

            <Card.Body>
              <form
                onSubmit={handleVerify}
                className="flex-column align-items-center justify-content-center verify-form-fullwidth"
              >
                <div className="code-input-wrapper">
                  <input
                    type="text"
                    className="totp-input"
                    placeholder={useBackupCode ? "12345678" : "000000"}
                    value={token}
                    onChange={handleInputChange}
                    maxLength={useBackupCode ? 8 : 6}
                    autoComplete="off"
                    autoFocus
                    disabled={loading}
                  />
                </div>

                {error && <p className="error-message">{error}</p>}

                <div className="button-group">
                  <button
                    className="verify-btn mt-3"
                    type="submit"
                    disabled={loading || token.length !== (useBackupCode ? 8 : 6)}
                  >
                    {loading ? "Verifying..." : "Verify"}
                  </button>

                  <button
                    type="button"
                    className="toggle-code-btn"
                    onClick={toggleCodeType}
                    disabled={loading}
                  >
                    {useBackupCode ? "Use authenticator code instead" : "Use backup code instead"}
                  </button>

                  <button
                    type="button"
                    className="back-to-login-btn"
                    onClick={() => navigate("/login")}
                    disabled={loading}
                  >
                    Back to Login
                  </button>
                </div>
              </form>
            </Card.Body>
          </Card>
        </div>
      </section>
    </div>
  );
}

export default TwoFactorVerifyPage;
